import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "../../lib/supabase"
import * as Sentry from "@sentry/react"
import { PartsIcon, BatteryIcon, FuelIcon, KeyIcon, OverheatIcon, TowingIcon, GOServiceIcon, LocationIcon, VehicleIcon, SearchIcon, WarningIcon, PhoneCallIcon, SignalIcon, CheckIcon, CloseIcon } from "../../lib/cccIcons"
import DarajaPayment from "../shared/DarajaPayment"
import { applyRateLimit, RATE_LIMITS } from "../../lib/rateLimit"
import { sanitizeAmount } from "../../lib/sanitize"
import { getCurrentPosition } from "../../lib/geolocation"
import PhotoManager from "../shared/PhotoManager"
import { useAuth } from "../../contexts/AuthContext"
import useIsMobile from "../../lib/useIsMobile"
import toast from "react-hot-toast"

const EMERGENCY_SERVICE_MAP = {
  flat_tire: ["tyre","tire","puncture","wheel","flat","rim","valve"],
  dead_battery: ["battery","jump","start","electrical","charge","alternator"],
  out_of_fuel: ["fuel","petrol","diesel","gas","empty tank"],
  car_wont_start: ["battery","electrical","ignition","start","mechanical","starter","spark"],
  overheating: ["cooling","overheat","radiator","water","temperature","diagnosis","coolant"],
  towing: ["tow","towing","recovery","transport","haul"],
  locked_out: ["lock","key","locked","locksmith","door"],
  accident: ["accident","collision","crash","dent","body","panel"],
  brake_failure: ["brake","braking","pad","disc","rotor"],
  warning_light: ["diagnostic","diagnosis","scan","warning","light","check engine","computer"],
  windshield_damage: ["windshield","windscreen","glass","crack","chip"],
  other: []
}

const EMERGENCY_TYPES = [
  { key:"flat_tire", label:"Flat tire", icon:"parts" },
  { key:"dead_battery", label:"Dead battery", icon:"battery" },
  { key:"out_of_fuel", label:"Out of fuel", icon:"fuel" },
  { key:"car_wont_start", label:"Car won't start", icon:"key" },
  { key:"overheating", label:"Overheating", icon:"overheat" },
  { key:"towing", label:"Need towing", icon:"towing" },
  { key:"locked_out", label:"Locked out", icon:"key" },
  { key:"accident", label:"Accident/collision", icon:"emergency" },
  { key:"brake_failure", label:"Brake failure", icon:"emergency" },
  { key:"warning_light", label:"Warning light on", icon:"overheat" },
  { key:"windshield_damage", label:"Windshield damage", icon:"parts" },
  { key:"other", label:"Other emergency", icon:"emergency" },
]

export default function CustomerGoService() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [step, setStep] = useState("select")
  const [emergencyType, setEmergencyType] = useState("")
  const [location, setLocation] = useState({ lat:null, lng:null, address:"" })
  const [assignedMechanic, setAssignedMechanic] = useState(null)
  const [mechanicETA, setMechanicETA] = useState(null)
  const [details, setDetails] = useState("")
  const [vehicle, setVehicle] = useState("")
  const [vehicles, setVehicles] = useState([])
  const [locating, setLocating] = useState(false)
  const [sharingLive, setSharingLive] = useState(false)
  const [liveInterval, setLiveInterval] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [reportingNoShow, setReportingNoShow] = useState(false)
  const [booking, setBooking] = useState(null)
  const [goRequest, setGoRequest] = useState(null)
  const [attemptNumber, setAttemptNumber] = useState(1)
  const [timeLeft, setTimeLeft] = useState(null)
  const [activeGoBookings, setActiveGoBookings] = useState([])
  const [goServices, setGoServices] = useState([])
  const [searchText, setSearchText] = useState("")
  const searchWords = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const emergencyKeywords = emergencyType ? (EMERGENCY_SERVICE_MAP[emergencyType]||[]) : []
  const allKeywords = [...emergencyKeywords, ...searchWords]
  const matchesService = (s) => allKeywords.some(k=>s.name?.toLowerCase().includes(k)||s.description?.toLowerCase().includes(k))
  const filteredGoServices = allKeywords.length
    ? goServices.filter(matchesService).concat(goServices.filter(s=>!matchesService(s)).map(s=>({...s,_dimmed:true})))
    : goServices
  const [selectedService, setSelectedService] = useState(null)
  // Auto-select the nearest matching GO service behind the scenes - the customer no longer
  // manually picks a provider; the system dispatches automatically via assign-go-provider
  useEffect(() => {
    if (!selectedService && filteredGoServices.length > 0) setSelectedService(filteredGoServices[0])
  }, [filteredGoServices])

  const [showDepositPayment, setShowDepositPayment] = useState(false)
  const [pendingGoBooking, setPendingGoBooking] = useState(null)
  const [showServiceFeePayment, setShowServiceFeePayment] = useState(false)
  const [payingCallout, setPayingCallout] = useState(false)
  const [calloutFee, setCalloutFee] = useState(500)
  const [calloutProviderRate, setCalloutProviderRate] = useState(0.70)
  const [serviceProviderRate, setServiceProviderRate] = useState(0.85)
  const [goPartsRequests, setGoPartsRequests] = useState([])
  useEffect(() => {
    if (user) {
      loadVehicles()
      loadActiveGoBookings()
      loadGoServices()
      // Load GO commission rates
      supabase.from("app_settings").select("key,value").in("key",["go_callout_fee","go_callout_provider_rate","go_service_provider_rate"]).then(({data})=>{
        data?.forEach(s=>{ if(s.key==="go_callout_fee") setCalloutFee(Number(s.value)); if(s.key==="go_callout_provider_rate") setCalloutProviderRate(Number(s.value)/100); if(s.key==="go_service_provider_rate") setServiceProviderRate(Number(s.value)/100) })
      })
    }
  }, [user])

  useEffect(() => {
    if (!booking) return
    supabase.from("go_parts_requests").select("*, inventory(name,price)").eq("booking_id", booking.id).then(({data})=>{ setGoPartsRequests(data||[]) }).catch(()=>{})
    const sub = supabase.channel(`go-booking-${booking.id}`)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"go_parts_requests", filter:`booking_id=eq.${booking.id}` },
        payload => {
          setGoPartsRequests(prev=>[...prev, payload.new])
          toast("🔧 Mechanic has requested a part", { icon:"🔧" })
        })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"go_parts_requests", filter:`booking_id=eq.${booking.id}` },
        payload => { setGoPartsRequests(prev=>prev.map(r=>r.id===payload.new.id?{...r,...payload.new}:r)) })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"bookings", filter:`id=eq.${booking.id}` },
        payload => {
          setBooking(b=>({...b,...payload.new}))
          if (payload.new.status==="completed") {
            setShowServiceFeePayment(true)
            toast.success("Service complete! Please pay the service fee 💳")
          }
          if (payload.new.status==="confirmed"||payload.new.status==="in-progress") {
            setStep("accepted")
            setTimeLeft(null) // Stop the countdown timer
            toast.success("Provider found! Help is on the way 🚨")
            // Load mechanic details
            if(payload.new.assigned_mechanic_id) fetchMechanicAndETA(payload.new.id||booking?.id)
          }
        })
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"go_service_requests", filter:`booking_id=eq.${booking.id}` },
        payload => {
          setGoRequest(payload.new)
          setAttemptNumber(payload.new.attempt_number)
          startCountdown(15 * 60)
        })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"go_service_requests", filter:`booking_id=eq.${booking.id}` },
        payload => {
          setGoRequest(payload.new)
          if (payload.new.status==="timeout") {
            setAttemptNumber(n=>n+1)
            if (attemptNumber >= 5) {
              setStep("no_providers")
              toast.error("No providers available at this time")
            } else {
              toast("Looking for next provider...", { icon:"🔍" })
              startCountdown(15 * 60) // Reset timer for next provider
              startCountdown(15 * 60)
            }
          }
        })
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [booking?.id, attemptNumber])

  useEffect(() => {
    if (timeLeft === null) return
    if (timeLeft <= 0) return
    const timer = setTimeout(() => setTimeLeft(t=>t-1), 1000)
    return () => clearTimeout(timer)
  }, [timeLeft])

  function startCountdown(seconds) { setTimeLeft(seconds) }

  async function loadVehicles() {
    const { data } = await supabase.from("vehicles").select("*").eq("user_id", user.id)
    setVehicles(data||[])
  }

  async function loadActiveGoBookings() {
    const { data } = await supabase.from("bookings")
      .select("*")
      .eq("customer_id", user.id)
      .eq("is_emergency", true)
      .not("status", "in", "(completed,cancelled)")
      .order("created_at", { ascending:false })
    setActiveGoBookings(data||[])
    if (data && data.length > 0 && data[0].go_callout_paid) {
      const active = data[0]
      const elapsed = Math.floor((Date.now() - new Date(active.created_at).getTime()) / 1000)
      const remaining = Math.max(0, 15*60 - elapsed)
      if (remaining > 0 && active.status === "pending") {
        setBooking(active)
        setStep("waiting")
        setTimeLeft(remaining)
      }
    }
  }
  function distanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371
    const dLat = (lat2-lat1) * Math.PI/180
    const dLon = (lon2-lon1) * Math.PI/180
    const a = Math.sin(dLat/2)*Math.sin(dLat/2) + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)*Math.sin(dLon/2)
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  }
  async function loadGoServices() {
    const { data } = await supabase.from("services")
      .select("*, profiles(business_name,first_name,last_name,city,latitude,longitude,go_service_radius_km,marketplace_rating,marketplace_review_count,is_online,carries_parts_for_go,go_parts_carried)")
      .eq("category", "go_service")
      .eq("is_active", true).eq("profiles.is_active", true)
    let services = data||[]
    if (location.lat && location.lng) {
      services = services.map(s => {
        const p = s.profiles
        const dist = (p?.latitude && p?.longitude) ? distanceKm(location.lat, location.lng, p.latitude, p.longitude) : null
        return { ...s, _distance: dist }
      }).filter(s => {
        if (s._distance === null) return true
        const radius = s.profiles?.go_service_radius_km || 15
        return s._distance <= radius
      }).sort((a,b) => (a._distance ?? 999) - (b._distance ?? 999))
    }
    setGoServices(services)
  }

  async function fetchMechanicAndETA(bookingId) {
    try {
      const { data: booking } = await supabase.from("bookings")
        .select("assigned_mechanic_id")
        .eq("id", bookingId).single()
      if (!booking?.assigned_mechanic_id) return
      const { data: mech } = await supabase.from("mechanics")
        .select("*, profiles!mechanics_provider_id_fkey(business_name)")
        .eq("id", booking.assigned_mechanic_id).single()
      if (mech) {
        setAssignedMechanic(mech)
        // Calculate ETA if mechanic has location
        if (mech.current_latitude && mech.current_longitude && location.lat && location.lng) {
          const R = 6371
          const dLat = (mech.current_latitude - location.lat) * Math.PI/180
          const dLng = (mech.current_longitude - location.lng) * Math.PI/180
          const a = Math.sin(dLat/2)*Math.sin(dLat/2) + Math.cos(location.lat*Math.PI/180)*Math.cos(mech.current_latitude*Math.PI/180)*Math.sin(dLng/2)*Math.sin(dLng/2)
          const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
          const etaMins = Math.round(dist / 30 * 60) // avg 30km/h in Nairobi traffic
          setMechanicETA(etaMins)
        }
      }
    } catch(e) { console.warn("ETA error:", e.message) }
  }

  async function detectLocation() {
    setLocating(true)
    try {
      const pos = await getCurrentPosition()
      const lat = pos.latitude
      const lng = pos.longitude
      setLocation(l=>({...l, lat, lng}))
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
        const data = await res.json()
        setLocation({ lat, lng, address:data.display_name||`${lat.toFixed(4)}, ${lng.toFixed(4)}` })
      } catch { setLocation(l=>({...l, address:`${lat.toFixed(4)}, ${lng.toFixed(4)}`})) }
    } catch(err) {
      toast.error(err.message || "Could not get location — please enter manually")
    } finally {
      setLocating(false)
    }
  }

  function startLiveSharing(bookingId) {
    if (liveInterval) return
    setSharingLive(true)
    const interval = setInterval(async () => {
      try {
        const pos = await getCurrentPosition()
        await supabase.from("booking_location_logs").insert({
          booking_id: bookingId,
          driver_id: null,
          lat: pos.latitude,
          lng: pos.longitude,
          source: "customer",
        })
      } catch(e) { console.warn("Live share error:", e.message) }
    }, 15000)
    setLiveInterval(interval)
  }
  function stopLiveSharing() {
    if (liveInterval) { clearInterval(liveInterval); setLiveInterval(null) }
    setSharingLive(false)
  }
  useEffect(() => {
    return () => { if (liveInterval) clearInterval(liveInterval) }
  }, [liveInterval])
  async function checkRateLimit() {
    const today = new Date().toISOString().split("T")[0]
    const { count } = await supabase.from("bookings").select("id",{count:"exact"}).eq("customer_id",user.id).eq("is_emergency",true).gte("created_at",today+"T00:00:00")
    return count||0
  }

  async function testBypassPayment() {
    if (!emergencyType) return toast.error("Please select emergency type")
    if (!location.lat) return toast.error("Please share your location")
    if (!selectedService) return toast.error("Please select a GO service")
    try {
      const { data: bk, error } = await supabase.from("bookings").insert({
        customer_id: user.id, provider_id: selectedService.provider_id,
        service_id: selectedService.id, service_name: selectedService.name,
        service_category: "go_service", is_emergency: true,
        emergency_type: emergencyType,
        emergency_location_lat: location.lat, emergency_location_lng: location.lng,
        emergency_location_address: location.address,
        booking_date: new Date().toISOString().split("T")[0],
        booking_time: new Date().toTimeString().slice(0,5),
        total_amount: calloutFee, go_callout_fee: calloutFee,
        go_callout_paid: true, payment_status: "paid", status: "pending",
        platform_commission: Number(selectedService.price)*(1-serviceProviderRate),
        provider_earnings: Number(selectedService.price)*serviceProviderRate,
        platform_commission_rate: (1-serviceProviderRate), provider_commission_rate: serviceProviderRate,
        payment_method: "test", notes: details, vehicle_id: vehicle||null, go_attempt_number: 1,
      }).select().single()
      if(error) throw error
      // Create go_service_request
      await supabase.from("go_service_requests").insert({ booking_id: bk.id, provider_id: selectedService.provider_id, status: "pending", attempt_number: 1 })
      await supabase.from("notifications").insert({ user_id: selectedService.provider_id, title: "🚨 Emergency GO Service request!", message: "Test request: "+emergencyType.replace(/_/g," ")+" at "+location.address.slice(0,60), type: "error" })
      setBooking(bk); setStep("waiting"); startCountdown(15*60)
      toast.success("Test booking created! (No payment charged)")
    } catch(e) { toast.error(e.message) }
  }
  async function initiateCalloutPayment() {
    if (!emergencyType) return toast.error("Please select emergency type")
    if (!location.lat) return toast.error("Please share your location")
    if (!selectedService) return toast.error("Please select a GO service")
    const todayCount = await checkRateLimit()
    if (todayCount >= 2) return toast.error("Maximum 2 emergency requests per day.")
    setShowDepositPayment(true)
  }

  async function payCalloutFee() {
    setPayingCallout(true)
    try {
      Sentry.captureMessage("GO_CREATE_PROOF: about to insert booking with status=pending, no provider_id", { level: "info" })
      console.log("GO CREATE PATH - PROOF-2026-08-17-02: about to insert with status=pending")
      const { data: bk, error } = await supabase.from("bookings").insert({
        customer_id: user.id,
        // provider_id intentionally left unset - assign-go-provider picks the nearest available
        // provider automatically after this booking is created, matching the intended dispatch model
        service_id: selectedService.id,
        service_name: selectedService.name,
        service_category: "go_service",
        is_emergency: true,
        emergency_type: emergencyType,
        emergency_location_lat: location.lat,
        emergency_location_lng: location.lng,
        emergency_location_address: location.address,
        booking_date: new Date().toISOString().split("T")[0],
        booking_time: new Date().toTimeString().slice(0,5),
        total_amount: calloutFee,
        go_callout_fee: calloutFee,
        go_callout_paid: false,
        go_service_fee: Number(selectedService.price),
        go_service_fee_paid: false,
        // These reflect the callout fee only - that's the only amount actually collected at this
        // point. The service fee's own commission split happens separately when it's paid later.
        platform_commission: Math.round(calloutFee*(1-calloutProviderRate)),
        provider_earnings: Math.round(calloutFee*calloutProviderRate),
        platform_commission_rate: (1-calloutProviderRate),
        provider_commission_rate: calloutProviderRate,
        payment_method: "mpesa",
        payment_status: "pending",
        status: "pending",
        notes: details,
        vehicle_id: vehicle||null,
        go_attempt_number: 1,
      }).select().single()
      if (error) throw error
      Sentry.captureMessage("GO_CREATE_PROOF: booking created", { level: "info", extra: { bookingId: bk?.id, bookingNumber: bk?.booking_number, actualStatus: bk?.status, actualProviderId: bk?.provider_id } })
      console.log("GO CREATE PROOF - booking returned from DB:", { id: bk?.id, status: bk?.status, provider_id: bk?.provider_id })
      // Provider matching now happens ONLY after the callout fee payment genuinely confirms
      // (triggered from daraja-callback), not here - dispatching before payment even completes
      // meant a provider could be notified for a request the customer might never actually pay for.
      // Show Daraja payment modal
      setPendingGoBooking({ id: bk.id, amount: calloutFee })
      setShowDepositPayment(false)
    } catch(e) { toast.error(e.message||"Failed to create booking") }
    finally { setPayingCallout(false) }
  }
  // payServiceFee removed - payment now triggers automatically via go-request-service-payment
  // when the mechanic marks the job complete, and releases via daraja-callback on real confirmation
  async function submitEmergency() {
    if (!emergencyType) return toast.error("Please select emergency type")
    if (!location.lat) return toast.error("Please share your location")
    if (!selectedService) return toast.error("Please select a GO service")
    setSubmitting(true)
    try {
      const { data: bk, error } = await supabase.from("bookings").insert({
        customer_id: user.id,
        provider_id: selectedService.provider_id,
        service_id: selectedService.id,
        service_name: selectedService.name,
        service_category: "go_service",
        is_emergency: true,
        emergency_type: emergencyType,
        emergency_location_lat: location.lat,
        emergency_location_lng: location.lng,
        emergency_location_address: location.address,
        booking_date: new Date().toISOString().split("T")[0],
        booking_time: new Date().toTimeString().slice(0,5),
        total_amount: selectedService.price,
        platform_commission: Number(selectedService.price)*(1-serviceProviderRate),
        provider_earnings: Number(selectedService.price)*serviceProviderRate,
        platform_commission_rate: (1-serviceProviderRate),
        provider_commission_rate: serviceProviderRate,
        payment_method: "mpesa",
        payment_status: "pending",
        status: "pending",
        notes: details,
        vehicle_id: vehicle||null,
        go_attempt_number: 1,
      }).select().single()
      if (error) throw error
      // Create go_service_request so providers can see it
      const { error: reqError } = await supabase.from("go_service_requests").insert({
        booking_id: bk.id,
        provider_id: selectedService.provider_id,
        status: "pending",
        attempt_number: 1,
      })
      if (reqError) {
        console.error("Failed to create go_service_request:", reqError.message)
        toast.error("Dispatch issue: " + reqError.message)
      }
      // Notify provider
      await supabase.from("notifications").insert({
        user_id: selectedService.provider_id,
        title: "🚨 Emergency GO Service request!",
        message: "A customer needs emergency help: "+emergencyType.replace(/_/g," ")+" at "+location.address.slice(0,60),
        type: "error"
      })
      setBooking(bk)
      setStep("waiting")
      startCountdown(15 * 60)
      toast.success("Emergency request sent! 🚨")
    } catch(err) {
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function cancelRequest() {
    if (!booking) return
    if (!confirm("Cancel this emergency request?")) return
    await supabase.from("bookings").update({ status:"cancelled" }).eq("id", booking.id)
    setBooking(null)
    setStep("select")
    setTimeLeft(null)
    toast.success("Request cancelled")
  }

  const formatTime = (seconds) => {
    if (!seconds) return "15:00"
    const m = Math.floor(seconds/60)
    const s = seconds%60
    return `${m}:${s.toString().padStart(2,"0")}`
  }

  const emergencyData = EMERGENCY_TYPES.find(e=>e.key===emergencyType)

  if (step==="waiting") return (
    <div>
      <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:16, padding:"1.5rem", marginBottom:"1rem", textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:10 }}>🚨</div>
        <div style={{ fontFamily:"Syne", fontSize:isMobile?18:22, fontWeight:800, color:"#e24b4a", marginBottom:6 }}>
          Emergency request sent
        </div>
        <div style={{ fontSize:13, color:"#555555", marginBottom:"1.5rem" }}>
          Looking for available providers...
        </div>

        <div style={{ background:"#ffffff", borderRadius:12, padding:"1.25rem", marginBottom:"1.5rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:36, fontWeight:800, color:timeLeft&&timeLeft<60?"#e24b4a":"#e6821e", letterSpacing:2, marginBottom:4 }}>
            {formatTime(timeLeft)}
          </div>
          <div style={{ fontSize:11, color:"#777777" }}>
            Time remaining for provider to accept · Attempt {attemptNumber} of 5
          </div>
          <div style={{ background:"#f5f5f5", borderRadius:8, height:6, marginTop:12, overflow:"hidden" }}>
            <div style={{ background:"#e6821e", height:"100%", width:`${((timeLeft||0)/(15*60))*100}%`, transition:"width 1s linear", borderRadius:8 }}/>
          </div>
        </div>

        <div style={{ background:"#ffffff", borderRadius:10, padding:"1rem", marginBottom:"1.5rem", textAlign:"left" }}>
          <div style={{ fontSize:11, color:"#777777", marginBottom:8 }}>Emergency details</div>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
            <span style={{ fontSize:20 }}>{emergencyData?.icon}</span>
            <span style={{ fontSize:13, color:"#000000" }}>{emergencyData?.label}</span>
          </div>
          <div style={{ fontSize:11, color:"#777777" }}>📍 {location.address||"Location shared"}</div>
          {details&&<div style={{ fontSize:11, color:"#777777", marginTop:4 }}>📝 {details}</div>}
        </div>

        </div>

      {/* Safety checklist */}
      <div style={{ background:"#ffffff", border:"1px solid #e6821e40", borderRadius:16, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:800, color:"#e6821e", marginBottom:12 }}>
          ⚠️ Stay safe while you wait
        </div>
        <div style={{ fontSize:12, color:"#555555", marginBottom:12, lineHeight:1.6 }}>
          Please take the following safety measures immediately while our mechanic is on the way:
        </div>
        {[
          { icon:"🔴", text:"Turn on your hazard lights immediately", priority:"high" },
          { icon:"⚠️", text:"Place warning triangles at least 50 metres behind your vehicle", priority:"high" },
          { icon:"🚗", text:"Move your vehicle to the hard shoulder or road side if possible", priority:"high" },
          { icon:"👥", text:"Keep all passengers away from the road — move to safety barrier side", priority:"high" },
          { icon:"📱", text:"Stay in your vehicle if you are on a highway or fast road", priority:"medium" },
          { icon:"🔦", text:"If at night — use your phone torch to stay visible to other drivers", priority:"medium" },
          { icon:"🚫", text:"Do not attempt to repair the vehicle yourself on a busy road", priority:"medium" },
          { icon:"📞", text:"If blocking traffic — call NTSA: 0800 723 573", priority:"emergency" },
          { icon:"🚔", text:"If you are in danger — call Police: 999", priority:"emergency" },
        ].map(item=>(
          <div key={item.text} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10, padding:"0.6rem", background:item.priority==="emergency"?"#fff5f5":item.priority==="high"?"#fff8f0":"#f5f5f5", borderRadius:8, border:`1px solid ${item.priority==="emergency"?"#fecaca":item.priority==="high"?"#fed7aa":"#eeeeee"}` }}>
            <span style={{ fontSize:18, flexShrink:0 }}>{item.icon}</span>
            <span style={{ fontSize:12, color:item.priority==="emergency"?"#e24b4a":item.priority==="high"?"#e6821e":"#888", lineHeight:1.5 }}>{item.text}</span>
          </div>
        ))}
        <div style={{ background:"#ffffff", borderRadius:8, padding:"0.75rem", marginTop:4 }}>
          <div style={{ fontSize:11, color:"#777777", lineHeight:1.6 }}>
            🇰🇪 Kenya Road Safety — In case of accident or emergency on a public road, you are required by law to place warning signs and alert other road users. Failure to do so may result in further accidents.
          </div>
        </div>
      </div>

      <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:16, padding:"1.5rem", marginBottom:"1rem", textAlign:"center" }}>
        <button onClick={cancelRequest}
          style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:10, color:"#e24b4a", fontSize:13, padding:"10px 24px", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
          Cancel request
        </button>
      </div>
    </div>
  )

  if (step==="accepted") return (
    <div>
      <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:16, padding:"1.5rem", marginBottom:"1rem", textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:10 }}>✅</div>
        <div style={{ fontFamily:"Syne", fontSize:isMobile?18:22, fontWeight:800, color:"#1d9e75", marginBottom:6 }}>
          Help is on the way!
        </div>
        <div style={{ fontSize:13, color:"#555555", marginBottom:"1.5rem" }}>
          A mechanic has been dispatched to your location
        </div>
        {assignedMechanic&&(
          <div style={{ background:"#ffffff", borderRadius:10, padding:"1rem", textAlign:"left", marginBottom:"1rem" }}>
            <div style={{ fontSize:11, color:"#777777", marginBottom:6 }}>Your mechanic</div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:"#f0fdf4", border:"2px solid #1d9e75", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>👨‍🔧</div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#000" }}>{assignedMechanic.first_name} {assignedMechanic.last_name}</div>
                <div style={{ fontSize:11, color:"#555" }}>{assignedMechanic.specialization}</div>
                <div style={{ fontSize:11, color:"#888" }}>{assignedMechanic.profiles?.business_name}</div>
              </div>
            </div>
            {mechanicETA&&(
              <div style={{ marginTop:10, background:"#f0fdf4", borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:20 }}>⏱️</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1d9e75" }}>ETA: ~{mechanicETA} minutes</div>
                  <div style={{ fontSize:10, color:"#555" }}>Based on current mechanic location</div>
                </div>
              </div>
            )}
            {assignedMechanic.phone&&(
              <a href={"tel:"+assignedMechanic.phone} style={{ display:"block", marginTop:8, background:"#1d9e75", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, padding:"8px 12px", textAlign:"center", textDecoration:"none" }}>
                📞 Call mechanic: {assignedMechanic.phone}
              </a>
            )}
          </div>
        )}
        <div style={{ background:"#ffffff", borderRadius:10, padding:"1rem", textAlign:"left", marginBottom:"1rem" }}>
          <div style={{ fontSize:11, color:"#777777", marginBottom:6 }}>Booking reference</div>
          <div style={{ fontSize:13, color:"#000000", fontFamily:"monospace" }}>#{booking?.booking_number}</div>
        </div>
        {(booking?.before_photo_url||booking?.after_photo_url)&&(
          <div style={{ background:"#ffffff", borderRadius:10, padding:"1rem", textAlign:"left", marginBottom:"1rem" }}>
            <div style={{ fontSize:11, color:"#777777", marginBottom:8 }}>Vehicle condition photos</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {booking?.before_photo_url&&(
                <div>
                  <img src={booking.before_photo_url} alt="Before" style={{ width:"100%", height:100, objectFit:"cover", borderRadius:8, border:"1px solid #eee" }}/>
                  <div style={{ fontSize:10, color:"#888", marginTop:4, textAlign:"center" }}>Before</div>
                </div>
              )}
              {booking?.after_photo_url&&(
                <div>
                  <img src={booking.after_photo_url} alt="After" style={{ width:"100%", height:100, objectFit:"cover", borderRadius:8, border:"1px solid #eee" }}/>
                  <div style={{ fontSize:10, color:"#888", marginTop:4, textAlign:"center" }}>After</div>
                </div>
              )}
            </div>
          </div>
        )}
        {goPartsRequests.length>0&&(
          <div style={{ background:"#f3f0ff", border:"1px solid #8b5cf640", borderRadius:12, padding:"1rem", marginBottom:"1rem", textAlign:"left" }}>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#8b5cf6", marginBottom:8 }}>🔧 Parts Request</div>
            {goPartsRequests.map(r=>(
              <div key={r.id} style={{ background:"#fff", borderRadius:8, padding:"0.75rem", marginBottom:6 }}>
                <div style={{ fontSize:13, fontWeight:600 }}>{r.inventory?.name} x{r.quantity}</div>
                <div style={{ fontSize:11, color:"#888" }}>KES {Number(r.total_amount).toLocaleString()}</div>
                {/* Delivery progress bar */}
                <div style={{ display:"flex", gap:4, marginTop:8, marginBottom:6 }}>
                  {["accepted","picked_up","on_the_way","delivered"].map((s,i)=>(
                    <div key={s} style={{ flex:1, height:3, borderRadius:3, background:["accepted","picked_up","on_the_way","delivered"].indexOf(r.delivery_status||r.status)>=i?"#8b5cf6":"#eee" }}/>
                  ))}
                </div>
                <div style={{ fontSize:11, fontWeight:600, color:r.delivery_status==="delivered"?"#1d9e75":r.delivery_status==="on_the_way"?"#378add":r.delivery_status==="picked_up"?"#e6821e":"#8b5cf6", marginTop:2 }}>
                  {r.delivery_status==="pending"||r.status==="pending"?"⏳ Awaiting supplier":
                   r.delivery_status==="accepted"?"✅ Supplier confirmed — preparing":
                   r.delivery_status==="picked_up"?"📦 Part picked up by rider":
                   r.delivery_status==="on_the_way"?"🚚 Rider on the way to you":
                   "✅ Part delivered — mechanic fitting it"}
                </div>
                {(r.delivery_status==="on_the_way"||r.delivery_status==="picked_up")&&r.rider_name&&(
                  <div style={{ fontSize:11, color:"#555", marginTop:4 }}>
                    🚴 Rider: {r.rider_name}
                    {r.rider_phone&&<a href={"tel:"+r.rider_phone} style={{ marginLeft:6, color:"#1d9e75", fontWeight:600 }}>{r.rider_phone}</a>}
                  </div>
                )}
                {r.delivery_status==="delivered"&&!r.payment_released&&(
                  <div style={{ display:"flex", gap:6, marginTop:8 }}>
                    <button onClick={async()=>{
                      await supabase.from("go_parts_requests").update({ payment_released:true, customer_approved:true, customer_approved_at:new Date().toISOString() }).eq("id",r.id)
                      await supabase.functions.invoke("daraja-stk-push",{ body:{ amount:r.total_amount, booking_id:r.booking_id, customer_id:user.id, provider_id:r.provider_id, service_name:"GO Parts: "+(r.inventory?.name||"Part") } })
                      setGoPartsRequests(prev=>prev.map(x=>x.id===r.id?{...x,payment_released:true}:x))
                      toast.success("Payment released to supplier! 💰")
                    }} style={{ flex:1, background:"#1d9e75", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontWeight:700, padding:"8px 10px", cursor:"pointer" }}>✅ Part Received — Pay</button>
                    <button onClick={async()=>{
                      await supabase.from("go_parts_requests").update({ status:"cancelled", delivery_status:"cancelled" }).eq("id",r.id)
                      await supabase.from("notifications").insert({ user_id:r.provider_id, title:"Part not received ❌", message:"Customer reported part was not received. Please investigate.", type:"error" })
                      setGoPartsRequests(prev=>prev.map(x=>x.id===r.id?{...x,status:"cancelled"}:x))
                      toast.error("Reported — refund will be initiated")
                    }} style={{ flex:1, background:"#fff5f5", border:"1px solid #fecaca", borderRadius:8, color:"#e24b4a", fontSize:11, fontWeight:700, padding:"8px 10px", cursor:"pointer" }}>❌ Not Received</button>
                  </div>
                )}
                {r.payment_released&&<div style={{ fontSize:11, color:"#1d9e75", fontWeight:600, marginTop:6 }}>✅ Payment released to supplier</div>}
              </div>
            ))}
          </div>
        )}
        <div style={{ background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"1rem", marginBottom:"1rem", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700 }}>Share my live location</div>
            <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{sharingLive?"Provider can see you moving in real-time":"Help the provider find you if you need to move"}</div>
          </div>
          <button onClick={()=>{ if(sharingLive){ stopLiveSharing() } else { startLiveSharing(booking?.id) } }}
            style={{ background:sharingLive?"#1d9e75":"#e6e6e6", border:"none", borderRadius:20, width:44, height:24, position:"relative", cursor:"pointer", flexShrink:0 }}>
            <div style={{ position:"absolute", top:2, left:sharingLive?22:2, width:20, height:20, background:"#fff", borderRadius:"50%", transition:"left 0.2s" }}/>
          </button>
        </div>
        <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:10, padding:"0.75rem", marginBottom:"1rem" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#e6821e", marginBottom:4 }}>⚠️ Payment Protection</div>
          <div style={{ fontSize:11, color:"#555", lineHeight:1.6 }}>Never pay the mechanic directly. All payments go through CCC for your protection. Direct payments void your warranty and refund rights.</div>
        </div>
        <div style={{ background:"#fff", border:"1px solid #eee", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:8 }}>Confirming your mechanic</div>
          <div style={{ fontSize:11, color:"#888", marginBottom:12, lineHeight:1.6 }}>When your mechanic arrives, they'll ask you for a verification code. Check your notifications for a 4-digit code and read it to them - this confirms it's genuinely your assigned mechanic before releasing payment.</div>
          <div style={{ display:"flex", gap:8 }}>
            <button disabled={reportingNoShow} onClick={async()=>{
              if (reportingNoShow) return
              setReportingNoShow(true)
              await supabase.from("go_provider_strikes").insert({ provider_id:booking?.provider_id, booking_id:booking?.id, reason:"no_show" })
              const { data, error } = await supabase.functions.invoke("go-refund-callout",{body:{booking_id:booking?.id, customer_id:user.id}})
              if (error) { toast.error("Something went wrong - please contact support if this persists."); setReportingNoShow(false); return }
              toast.error("No-show reported. Refund initiated. 💸")
            }} style={{ flex:1, background:reportingNoShow?"#f5f5f5":"#fff5f5", border:"1px solid #fecaca", borderRadius:8, color:"#e24b4a", fontSize:12, fontWeight:700, padding:"10px", cursor:reportingNoShow?"not-allowed":"pointer" }}>{reportingNoShow?"Reporting...":"❌ No show"}</button>
          </div>
        </div>
        <button onClick={()=>{ setStep("select"); setBooking(null); loadActiveGoBookings() }}
          style={{ background:"#f8f8f8", border:"1px solid #eee", borderRadius:10, color:"#555", fontFamily:"Syne,sans-serif", fontSize:13, padding:"11px 24px", cursor:"pointer" }}>
          Back to GO Service
        </button>
      </div>
    </div>
  )

  if (step==="no_providers") return (
    <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:16, padding:"2rem", textAlign:"center" }}>
      <div style={{ fontSize:40, marginBottom:10 }}>😔</div>
      <div style={{ fontFamily:"Syne", fontSize:18, fontWeight:800, color:"#e24b4a", marginBottom:8 }}>No providers available</div>
      <div style={{ fontSize:13, color:"#555555", marginBottom:"1.5rem", lineHeight:1.6 }}>
        We tried 5 providers but none were available right now. Please try again in a few minutes or call emergency services if urgent.
      </div>
      <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
        <button onClick={()=>{ setStep("select"); setBooking(null); setAttemptNumber(1); setTimeLeft(null) }}
          style={{ background:"#e6821e", border:"none", borderRadius:10, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 24px", cursor:"pointer" }}>
          Try again
        </button>
        <a href="tel:999" style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:10, color:"#e24b4a", fontSize:13, fontWeight:600, padding:"11px 24px", textDecoration:"none", fontFamily:"'DM Sans',sans-serif" }}>
          📞 Call emergency
        </a>
      </div>
    </div>
  )

  return (
    <div>
      {/* Active GO bookings */}
      {activeGoBookings.length>0&&(
        <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:"1rem", marginBottom:"1.5rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, color:"#1d9e75", marginBottom:8 }}>Active emergency requests</div>
          {activeGoBookings.map(b=>(
            <div key={b.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #eeeeee" }}>
              <div>
                <div style={{ fontSize:12, color:"#000000" }}>{b.service_name}</div>
                <div style={{ fontSize:10, color:"#777777" }}>#{b.booking_number} · {b.status}</div>
              </div>
              <button onClick={()=>{ setBooking(b); setStep(b.status==="confirmed"||b.status==="in-progress"?"accepted":"waiting"); if(b.assigned_mechanic_id) fetchMechanicAndETA(b.id) }}
                style={{ background:"#e6821e", border:"none", borderRadius:7, color:"#fff", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                Track
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:16, padding:"1.25rem", marginBottom:"1.5rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
          <span style={{ fontSize:24 }}>🚨</span>
          <div>
            <div style={{ fontFamily:"Syne", fontSize:16, fontWeight:800, color:"#e24b4a" }}>GO Services — Emergency</div>
            <div style={{ fontSize:11, color:"#666" }}>24/7 roadside assistance · Online payment only</div>
          </div>
        </div>
        <div style={{ fontSize:11, color:"#777777", lineHeight:1.6 }}>
          Provider has 15 minutes to accept. We try up to 5 providers before notifying you of unavailability.
        </div>
      </div>

      {/* Step 1 — Emergency type */}
      <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000000", marginBottom:"1rem" }}>
          1. What is your emergency?
        </div>
        <input value={searchText} onChange={e=>setSearchText(e.target.value)}
          placeholder="Or describe it in your own words - e.g. 'grinding noise when I brake'..."
          style={{ width:"100%", background:"#f8f8f8", border:"1px solid #e5e5e5", borderRadius:8, padding:"10px 12px", color:"#000000", fontSize:12, outline:"none", fontFamily:"'DM Sans',sans-serif", marginBottom:12, boxSizing:"border-box" }}/>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)", gap:8 }}>
          {EMERGENCY_TYPES.map(e=>(
            <button key={e.key} onClick={()=>setEmergencyType(e.key)}
              style={{ background:emergencyType===e.key?"#fff5f5":"#f5f5f5", border:`1px solid ${emergencyType===e.key?"#e24b4a":"#e5e5e5"}`, borderRadius:10, padding:"0.75rem", cursor:"pointer", textAlign:"center" }}>
              <div style={{ marginBottom:4, display:"flex", justifyContent:"center" }}>
              {e.icon==="parts"?<PartsIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               e.icon==="battery"?<BatteryIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               e.icon==="fuel"?<FuelIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               e.icon==="key"?<KeyIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               e.icon==="overheat"?<OverheatIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               e.icon==="towing"?<TowingIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>:
               <GOServiceIcon size={24} color={emergencyType===e.key?"#e24b4a":"#64748B"}/>}
            </div>
              <div style={{ fontSize:11, color:emergencyType===e.key?"#e24b4a":"#555555", fontWeight:emergencyType===e.key?600:400 }}>{e.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2 — Location */}
      <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000000", marginBottom:"1rem" }}>
          2. Your location
        </div>
        <button onClick={detectLocation} disabled={locating}
          style={{ background:locating?"#f0f0f0":"#fff5f5", border:"1px solid #fecaca", borderRadius:9, color:locating?"#999":"#e24b4a", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"11px 20px", cursor:locating?"not-allowed":"pointer", width:"100%", marginBottom:10 }}>
          {locating?"📍 Detecting...":<><LocationIcon size={14} color="#e24b4a"/> Use my current location</>}
        </button>
        {location.address&&(
          <div style={{ background:"#ffffff", borderRadius:8, padding:"0.75rem", fontSize:12, color:"#555555" }}>
            📍 {location.address}
          </div>
        )}
        {!location.lat&&(
          <div style={{ marginTop:10 }}>
            <input placeholder="Or enter your location manually..." value={location.address} onChange={e=>setLocation(l=>({...l,address:e.target.value}))}
              style={{ width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"11px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
          </div>
        )}
      </div>

      {/* Step 3 - Provider is matched automatically after payment, not selected manually */}
      <div style={{ background:"#fff8f0", border:"1px solid #e6821e40", borderRadius:12, padding:"1.25rem", marginBottom:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000000", marginBottom:6 }}>
          3. We'll find the nearest provider for you
        </div>
        <div style={{ fontSize:12, color:"#666666", lineHeight:1.6 }}>
          {filteredGoServices.length>0
            ? `${filteredGoServices.length} provider${filteredGoServices.length===1?"":"s"} available in your area. After payment, we'll automatically dispatch to the nearest one.`
            : "No GO services available in your area right now."}
        </div>
      </div>
      {/* Step 4 — Vehicle + details */}
      <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1.5rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, color:"#000000", marginBottom:"1rem" }}>
          4. Vehicle & details
        </div>
        {vehicles.length>0&&(
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }}>Your vehicle</label>
            <select value={vehicle} onChange={e=>setVehicle(e.target.value)}
              style={{ width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"11px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif" }}>
              <option value="">Select vehicle</option>
              {vehicles.map(v=><option key={v.id} value={v.id}>{v.make} {v.model} {v.year} — {v.license_plate}</option>)}
            </select>
          </div>
        )}
        <label style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }}>Additional details</label>
        <textarea value={details} onChange={e=>setDetails(e.target.value)}
          placeholder="Describe your situation, car color, exact location details..."
          style={{ width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"11px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"vertical", minHeight:80 }}/>
      </div>

      {/* Submit */}
      <button onClick={initiateCalloutPayment} disabled={submitting||!emergencyType||!location.lat||!selectedService}
        style={{ width:"100%", background:submitting||!emergencyType||!location.lat||!selectedService?"#555555":"#e24b4a", border:"none", borderRadius:12, color:submitting||!emergencyType||!location.lat||!selectedService?"#555":"#fff", fontFamily:"Syne,sans-serif", fontSize:15, fontWeight:700, padding:"15px", cursor:submitting||!emergencyType||!location.lat||!selectedService?"not-allowed":"pointer" }}>
        {submitting?"Sending request...":"🚨 Request Emergency Help"}
      </button>
      <div style={{ fontSize:11, color:"#888888", textAlign:"center", marginTop:8 }}>
        Online payment only · Provider has 15 min to accept · Max 5 attempts
      </div>
      {profile?.is_test_account&&(
        <button onClick={testBypassPayment} style={{ width:"100%", background:"#f8f8f8", border:"1px dashed #ccc", borderRadius:12, color:"#888", fontSize:12, padding:"10px", cursor:"pointer", marginTop:8 }}>
          🧪 Test Mode — Skip Payment
        </button>
      )}

      {showDepositPayment&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ width:"100%", maxWidth:400, background:"#ffffff", border:"1px solid #e24b4a40", borderRadius:16, padding:"1.5rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:16, fontWeight:800, color:"#e24b4a", marginBottom:4 }}>🚨 Confirm Emergency Request</div>
            <div style={{ fontSize:12, color:"#555555", marginBottom:16, lineHeight:1.6 }}>
              A KES {calloutFee} mechanic callout fee is required to dispatch a mechanic to your location. This covers transport costs.
            </div>
            <div style={{ background:"#ffffff", borderRadius:10, padding:"1rem", marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#555555", marginBottom:6 }}>
                <span>Emergency type</span><span style={{ color:"#000000" }}>{emergencyType.replace(/_/g," ")}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#555555", marginBottom:6 }}>
                <span>Provider</span><span style={{ color:"#000000" }}>Nearest available (auto-assigned)</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#555555", marginBottom:6 }}>
                <span>Service fee</span><span style={{ color:"#000000" }}>Set by assigned provider</span>
              </div>
              <div style={{ height:1, background:"#f0f0f0", margin:"8px 0" }}/>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, color:"#e6821e", fontWeight:700 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, color:"#e6821e", fontWeight:700 }}><span>Callout fee (pay now)</span><span>KES {calloutFee.toLocaleString()}</span></div>
              </div>
              <div style={{ fontSize:10, color:"#777777", marginTop:4 }}>Service fee paid after completion</div>
            </div>
            <button onClick={payCalloutFee} disabled={payingCallout}
              style={{ width:"100%", background:payingCallout?"#555555":"#e24b4a", border:"none", borderRadius:10, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:700, padding:"13px", cursor:payingCallout?"not-allowed":"pointer", marginBottom:8 }}>
              {payingCallout?"Sending M-Pesa prompt...":`Pay KES ${calloutFee} & Request Help →`}
            </button>
            <button onClick={()=>setShowDepositPayment(false)}
              style={{ width:"100%", background:"none", border:"1px solid #dddddd", borderRadius:10, color:"#666", fontSize:13, padding:"11px", cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {showServiceFeePayment&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ width:"100%", maxWidth:400, background:"#ffffff", border:"1px solid #1d9e7540", borderRadius:16, padding:"1.5rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:16, fontWeight:800, color:"#1d9e75", marginBottom:4 }}>✅ Service Complete!</div>
            <div style={{ fontSize:12, color:"#555555", marginBottom:16, lineHeight:1.6 }}>
              An M-Pesa payment request for the service fee has been sent to your phone. Please enter your PIN to complete payment.
            </div>
            <div style={{ background:"#f8f8f8", borderRadius:10, padding:"1rem", marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, color:"#1d9e75", fontWeight:700 }}>
                <span>Service fee</span><span>KES {Number(booking?.go_service_fee||0).toLocaleString()}</span>
              </div>
            </div>
            <button onClick={()=>setShowServiceFeePayment(false)}
              style={{ width:"100%", background:"none", border:"1px solid #dddddd", borderRadius:10, color:"#666", fontSize:13, padding:"11px", cursor:"pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}
    {pendingGoBooking&&(
      <DarajaPayment
        amount={pendingGoBooking.amount}
        bookingId={pendingGoBooking.id}
        providerId={pendingGoBooking.provider_id}
        description={"GO Service callout fee"}
        onSuccess={()=>{ setPendingGoBooking(null); const bk = {id:pendingGoBooking.id}; setBooking(bk); setStep("waiting"); startCountdown(15*60) }}
        onClose={()=>{ setPendingGoBooking(null); supabase.from("bookings").delete().eq("id",pendingGoBooking.id) }}
      />
    )}
    </div>
  )
}




















